// Imports
import { Clear, ZoomIn } from "@mui/icons-material";
import { Fragment, useCallback, useEffect, useRef, useState } from "react"
import { Colors } from "../../config/Constants";
import { OrderUploadLabels } from "../utils/Models";
import QuickPinchZoom, { make3dTransformValue } from "react-quick-pinch-zoom";
import { Box, CircularProgress, Modal, Typography, useMediaQuery, useTheme } from "@mui/material";
import { GobikesApi } from "../../config/Apis";
import { getAuthCookie } from "../auth/AuthUtil";

// Constants
const LabelToTextMapper = {
  [OrderUploadLabels.CUSTOMER]: 'Customer',
  [OrderUploadLabels.BIKE_BACK]: 'Bike back',
  [OrderUploadLabels.BIKE_FRONT]: 'Bike front',
  [OrderUploadLabels.BIKE_LEFT]: 'Bike left',
  [OrderUploadLabels.BIKE_RIGHT]: 'Bike right',
  [OrderUploadLabels.ODOMETER]: 'Odometer',
  [OrderUploadLabels.OTHER]: 'Other',
  [OrderUploadLabels.EXTRA_1]: 'Extra 1',
  [OrderUploadLabels.EXTRA_2]: 'Extra 2',
}

// Individual upload detail component
export function UploadImageViewer(props: {
  label: OrderUploadLabels
  awsKey: string
  customLabel?: string
}) {
  const displayLabel = props.customLabel || LabelToTextMapper[props.label];

  // Local state
  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [imageData, setImageData] = useState<string | undefined>();

  // Gloobals and constants
  const authCookie = getAuthCookie();
  const theme = useTheme();
  const isPhone = !useMediaQuery(theme.breakpoints.up('md'));

  // Image reference
  const imgRef = useRef<HTMLImageElement>(null);

  // Load data if required
  useEffect(() => {
    (async () => {
      if (open && !imageData) {
        setLoading(true);
        try {
          const { data } = await GobikesApi.get(`/vendors/uploads`, {
            params: { key: props.awsKey, loadData: true },
            headers: { 'vendor-token': authCookie }
          });
          if (data.data && data.data.uploadDetails && data.data.uploadDetails.data && data.data.base64String) {
            setImageData(`data:${data.data.uploadDetails.data.ContentType};base64,${data.data.base64String}`);
          } else {
            setError('Failed to load data');
          }
          setLoading(false);
        } catch (e: any) {
          const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
          setLoading(false);
          setError(errorMessage);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, imageData]);

  // Zoom on update function
  const onUpdate = useCallback(({ x, y, scale }) => {
    const { current: img } = imgRef;
    if (img) {
      const value = make3dTransformValue({ x, y, scale });
      img.style.setProperty("transform", value);
    }
  }, []);

  // Render base content
  function renderBaseContent() {
    return (
      <div className={`gbk-upload done`} style={{ marginBottom: '20px' }}>
        <div
          className='gbk-uploaded-img viewer'
          onClick={() => {
            setOpen(true);
          }}
        >
          <label className='gbk-icon-span large roboto' >
            <div>
              <div className="label">
                {displayLabel}
              </div>
              <div className="body">
                <span className="gbk-icon-span link-icon" style={{ color: Colors.primary, lineHeight: '22px' }}> <ZoomIn /></span>
                <span style={{ color: Colors.greyDark3 }}>Click to view</span>
              </div>
            </div>
          </label>
        </div>
      </div>
    )
  }

  // Render modal
  function renderModal() {
    const marginSize = isPhone ? 40 : 100;
    return (
      <Modal
        open={open}
        onClose={() => { setOpen(false) }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            backgroundColor: 'rgba(0,0,0,0.5)',
            margin: 'auto',
            marginTop: `${marginSize}px`,
            height: `calc(100% - ${marginSize * 2}px)`,
            width: `calc(100% - ${marginSize}px)`,
            borderRadius: '10px'
          }}
        >
          {loading || error ? (
            <div style={{ margin: 'auto', textAlign: 'center', paddingTop: '30%' }}>
              {error ? <Typography style={{ color: '#fff' }}>{error}</Typography> : <CircularProgress />}
            </div>
          ) : (
            <QuickPinchZoom onUpdate={onUpdate} centerContained={true}>
              <img alt={displayLabel} style={{ height: '80vh', width: 'auto' }} ref={imgRef} src={imageData} />
            </QuickPinchZoom>
          )}
          <div
            style={{
              background: Colors.primary,
              display: 'inline-block',
              position: 'absolute',
              top: `${marginSize + 10}px`,
              right: `${marginSize / 2 + 10}px`,
              height: '25px',
              width: '25px',
              cursor: 'pointer',
              borderRadius: '25px'
            }}
            onClick={() => { setOpen(false) }}
          >
            <Clear htmlColor={'#fff'} />
          </div>
        </Box>
      </Modal>
    )
  }

  // Return DOM
  return (
    <Fragment>
      {renderBaseContent()}
      {renderModal()}
    </Fragment>
  )
}